import {PageHeader} from '@primer/react/experimental'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import type {PullRequestState} from '../types'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import type {Repository} from '@github-ui/current-repository'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {clsx} from 'clsx'
import styles from './StickyPullRequestHeader.module.css'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'

export const condensedResponsiveWrapperClasses = 'container-xl tmp-px-3 tmp-px-md-4 tmp-px-lg-5'
export const fullWidthResponsiveWrapperClasses = 'tmp-px-3 tmp-px-md-4 tmp-px-lg-5'

export interface StickyPullRequestHeaderData {
  author: {
    login: string
    displayName?: string | null
  }
  attributedUser?: {
    login: string
    displayName?: string | null
    avatarUrl?: string | null
  } | null
  baseBranch: string
  commitsCount: number
  headBranch: string
  headRepositoryOwnerLogin?: string
  headRepositoryName?: string
  id: number
  isInAdvisoryRepo: boolean
  mergedBy?: string | null
  mergedByName?: string | null
  number: number
  state: PullRequestState
  title: string
  url: string
  titleHtml: SafeHTMLString
}

export interface StickyPullRequestHeaderProps {
  repository: Repository
  pullRequest: StickyPullRequestHeaderData
  pageVariant: 'condensed' | 'full-width'
}

export function StickyPullRequestHeader({repository, pullRequest, pageVariant}: StickyPullRequestHeaderProps) {
  const {isSticky, sensorRef} = useStickyHeader()
  const responsiveWrapperClasses =
    pageVariant === 'condensed' ? condensedResponsiveWrapperClasses : fullWidthResponsiveWrapperClasses

  return (
    <>
      <PageHeader
        className={clsx(
          stickyStyles.stickyHeader,
          styles.prHeader,
          isSticky && styles['is-stuck'],
          'color-shadow-small',
        )}
      >
        <PageHeader.TitleArea
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('flex-items-center justify-center', responsiveWrapperClasses, styles.prTitleArea)}
        >
          <PageHeader.LeadingVisual>
            <PullRequestStateLabel state={pullRequest.state} />
          </PageHeader.LeadingVisual>
          <PageHeader.Title className="lh-condensed">
            <div>
              <a href="#top" className="fgColor-default">
                <VerifiedHTMLText className="f5 text-bold markdown-title" html={pullRequest.titleHtml} />
              </a>
              <span className="f5 text-normal pl-2 fgColor-muted d-inline">#{pullRequest.number}</span>
              <div className="f6 text-normal">
                <PullRequestHeaderSummary
                  author={pullRequest.author}
                  attributedUser={pullRequest.attributedUser}
                  baseBranch={pullRequest.baseBranch}
                  baseRepositoryOwnerLogin={repository.ownerLogin}
                  baseRepositoryName={repository.name}
                  commitsCount={pullRequest.commitsCount}
                  headBranch={pullRequest.headBranch}
                  headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                  headRepositoryName={pullRequest.headRepositoryName}
                  isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  mergedBy={pullRequest.mergedBy}
                  mergedByName={pullRequest.mergedByName}
                  pullRequestId={pullRequest.id}
                  pullRequestNumber={pullRequest.number}
                  repositoryId={repository.id}
                  state={pullRequest.state}
                />
              </div>
            </div>
          </PageHeader.Title>
        </PageHeader.TitleArea>
      </PageHeader>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
    </>
  )
}
